import { useQuery } from '@tanstack/react-query';

import { DealsDashboardClient } from '@/shared/api';

/** Kept in the module api/, not Constants.tsx — matches DDS InviteEligibilityRoute. */
export const INVITE_ELIGIBILITY_PATH = '/v1/invite/eligibility';

export type InviteEligibilityOutcome = 'allow' | 'warning' | 'block';

export type InviteEligibilityMatch = {
  source: string;
  record_id: string;
  deal_id?: string | null;
  property_id?: string | null;
  address_id?: string | null;
  unit?: string;
  status_group: string;
  lease_end?: string | null;
  updated_at: string;
  contributed: boolean;
};

export type InviteEligibilityApplicant = {
  index: number;
  email: string;
  outcome: InviteEligibilityOutcome;
  reason: string;
  total_matches: number;
  truncated: boolean;
  matches: InviteEligibilityMatch[];
};

export type InviteEligibilityResponseData = {
  outcome: InviteEligibilityOutcome;
  match_sources?: string;
  applicants: InviteEligibilityApplicant[];
};

/**
 * Query params for GET /v1/invite/eligibility.
 * Emails are sent as repeated `emails=` keys (collectionFormat: multi).
 */
export type GetInviteEligibilityParams = {
  property_id: string;
  emails: string[];
  address_id?: string;
  unit?: string;
  unit_id?: string;
};

type GetInviteEligibilityResponse = {
  message: string;
  status_code: number;
  data: InviteEligibilityResponseData;
};

const normalizeOptional = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

/** Stable, trimmed inputs for the query key so stale results drop when lease/emails change. */
export const normalizeInviteEligibilityParams = (
  params: GetInviteEligibilityParams,
): GetInviteEligibilityParams => ({
  property_id: params.property_id.trim(),
  // Lowercase so casing-only edits share one query key / eligibility request.
  emails: params.emails.map((email) => email.trim().toLowerCase()).filter(Boolean),
  address_id: normalizeOptional(params.address_id),
  unit: normalizeOptional(params.unit),
  unit_id: normalizeOptional(params.unit_id),
});

export const inviteEligibilityQueryKey = (params: GetInviteEligibilityParams) => {
  const normalized = normalizeInviteEligibilityParams(params);
  return [
    'invite-eligibility',
    normalized.property_id,
    normalized.address_id ?? '',
    normalized.unit ?? '',
    normalized.unit_id ?? '',
    normalized.emails,
  ] as const;
};

/** Axios default array encoding uses `emails[]=`; DDS/Echo expect `emails=a&emails=b`. */
const serializeInviteEligibilityParams = (params: Record<string, unknown>): string => {
  const normalized = normalizeInviteEligibilityParams(params as GetInviteEligibilityParams);
  const searchParams = new URLSearchParams();
  searchParams.set('property_id', normalized.property_id);
  if (normalized.address_id) {
    searchParams.set('address_id', normalized.address_id);
  }
  if (normalized.unit) {
    searchParams.set('unit', normalized.unit);
  }
  if (normalized.unit_id) {
    searchParams.set('unit_id', normalized.unit_id);
  }
  for (const email of normalized.emails) {
    searchParams.append('emails', email);
  }
  return searchParams.toString();
};

export const getInviteEligibility = async (
  params: GetInviteEligibilityParams,
): Promise<InviteEligibilityResponseData> => {
  const { data } = await DealsDashboardClient.get<GetInviteEligibilityResponse>(
    INVITE_ELIGIBILITY_PATH,
    {
      params: normalizeInviteEligibilityParams(params),
      paramsSerializer: serializeInviteEligibilityParams,
    },
  );
  return data.data;
};

export type UseInviteEligibilityParams = {
  propertyId: string | undefined;
  emails: string[];
  addressId?: string;
  unit?: string;
  unitId?: string;
  /**
   * Gate the request (Split flag, drawer open, etc.). Still requires a property
   * and at least one email before a network call is made.
   */
  enabled?: boolean;
};

export const useInviteEligibility = ({
  propertyId,
  emails,
  addressId,
  unit,
  unitId,
  enabled = true,
}: UseInviteEligibilityParams) => {
  const params: GetInviteEligibilityParams | undefined = propertyId
    ? {
        property_id: propertyId,
        emails,
        address_id: addressId,
        unit,
        unit_id: unitId,
      }
    : undefined;

  const normalized = params ? normalizeInviteEligibilityParams(params) : undefined;
  const canQuery = Boolean(normalized?.property_id && normalized.emails.length > 0);

  return useQuery<InviteEligibilityResponseData>({
    queryKey: normalized
      ? inviteEligibilityQueryKey(normalized)
      : (['invite-eligibility', 'idle'] as const),
    queryFn: () => {
      if (!normalized) {
        return Promise.reject(new Error('Invite eligibility params are missing'));
      }
      return getInviteEligibility(normalized);
    },
    enabled: enabled && canQuery,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
